import React, { useMemo, useState } from "react";

const GROUPS = [
  {
    key: "base",
    label: "Base",
    title: "Base cabinets",
    match: ["cabinet.base", "cabinet.box"],
  },
  {
    key: "wall",
    label: "Wall",
    title: "Wall cabinets",
    match: ["cabinet.wall"],
  },
  {
    key: "tall",
    label: "Tall",
    title: "Tall cabinets",
    match: ["cabinet.tall"],
  },
  {
    key: "templates",
    label: "Templates",
    title: "Saved templates",
    match: [],
  },
  {
    key: "hardware",
    label: "Hardware",
    title: "Hardware",
    match: ["hardware."],
  },
];

const QUICK_SIZES = {
  base: [
    { label: "450", width: 450, height: 720, depth: 560 },
    { label: "600", width: 600, height: 720, depth: 560 },
    { label: "800", width: 800, height: 720, depth: 560 },
  ],
  wall: [
    { label: "450", width: 450, height: 700, depth: 320, floorOffset: 1400 },
    { label: "600", width: 600, height: 700, depth: 320, floorOffset: 1400 },
    { label: "800", width: 800, height: 700, depth: 320, floorOffset: 1400 },
  ],
  tall: [
    { label: "600", width: 600, height: 2000, depth: 560 },
    { label: "800", width: 800, height: 2000, depth: 560 },
  ],
};

function matchesGroup(item, group) {
  const category = String(item.category || "").toLowerCase();

  if (group.key === "templates") {
    return item.__type === "template";
  }

  return group.match.some((match) => category.includes(match));
}

function getItemTitle(item) {
  return item.name || item.code || "Unnamed item";
}

function getItemMeta(item) {
  return item.category || item.code || "";
}

export default function DesignerLibraryPanel({
  definitions = [],
  templates = [],
  onAdd,
  onAddTemplate,
}) {
  const [activeGroup, setActiveGroup] = useState("base");
  const [search, setSearch] = useState("");

  const activeGroupDef =
    GROUPS.find((group) => group.key === activeGroup) || GROUPS[0];

  const items = useMemo(() => {
    const source =
      activeGroup === "templates"
        ? templates.map((template) => ({
            ...template,
            __type: "template",
          }))
        : definitions;

    const q = search.trim().toLowerCase();

    return source.filter((item) => {
      if (!matchesGroup(item, activeGroupDef)) return false;

      if (!q) return true;

      return (
        String(item.name || "").toLowerCase().includes(q) ||
        String(item.code || "").toLowerCase().includes(q) ||
        String(item.category || "").toLowerCase().includes(q)
      );
    });
  }, [definitions, templates, activeGroup, activeGroupDef, search]);

  const quickSizes = QUICK_SIZES[activeGroup] || [];

  function addItem(item) {
    if (item.__type === "template") {
      onAddTemplate?.(item);
      return;
    }

    onAdd?.(item);
  }

  function addItemWithSize(item, size) {
    onAdd?.(item, {
      parameterOverrides: {
        width: size.width,
        height: size.height,
        depth: size.depth,
        ...(size.floorOffset !== undefined
          ? { floorOffset: size.floorOffset }
          : {}),
      },
    });
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-white">
      <div className="shrink-0 border-b p-3 space-y-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Add element
          </div>
          <div className="font-semibold text-sm text-slate-800">
            {activeGroupDef.title}
          </div>
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search library..."
          className="w-full px-2 py-1.5 border rounded text-sm"
        />

        <div className="grid grid-cols-2 gap-1">
          {GROUPS.map((group) => (
            <button
              key={group.key}
              type="button"
              onClick={() => setActiveGroup(group.key)}
              className={[
                "px-2 py-1.5 rounded border text-xs text-left",
                activeGroup === group.key
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100",
              ].join(" ")}
            >
              {group.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-2 space-y-2">
        {items.length ? (
          items.map((item) => (
            <div
              key={`${item.__type || "def"}_${item.id}`}
              className="rounded-lg border bg-white hover:border-blue-200 hover:bg-blue-50 transition p-3"
            >
              <button
                type="button"
                onClick={() => addItem(item)}
                className="w-full text-left"
              >
                <div className="font-medium text-sm text-slate-800 truncate">
                  {getItemTitle(item)}
                </div>

                <div className="text-xs text-slate-400 truncate">
                  {getItemMeta(item)}
                </div>
              </button>

              {item.__type !== "template" && quickSizes.length > 0 && (
                <div className="grid grid-cols-3 gap-1 mt-3">
                  {quickSizes.map((size) => (
                    <button
                      key={size.label}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        addItemWithSize(item, size);
                      }}
                      className="px-2 py-1.5 rounded border bg-slate-50 text-[11px] hover:bg-white"
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="rounded-lg border bg-slate-50 p-4 text-sm text-slate-400">
            No items in this group.
          </div>
        )}
      </div>
    </div>
  );
}